<template>
  <div class="job-category-scoring" data-testid="job-category-scoring">
    <!-- Section header: title + supporting text + Cancel/Save actions. -->
    <div class="job-category-scoring__header">
      <div class="job-category-scoring__header-text">
        <h2 class="job-category-scoring__title">Job Category Scoring</h2>
        <p class="job-category-scoring__subtitle">
          How each EIGE factor contributes to a Job Category's overall Total Score, and the value
          bands that Total Score is grouped into. See Settings — Jobs.
        </p>
      </div>
      <div class="job-category-scoring__actions">
        <button
          type="button"
          class="job-category-scoring__button job-category-scoring__button--cancel"
          data-testid="job-scoring-cancel"
          :disabled="loading || saving"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="job-category-scoring__button job-category-scoring__button--save"
          data-testid="job-scoring-save"
          :disabled="!canSave || saving"
          @click="handleSave"
        >
          {{ saving ? "Saving…" : "Save" }}
        </button>
      </div>
    </div>

    <p
      v-if="errorMessage"
      class="job-category-scoring__error"
      role="alert"
      data-testid="job-scoring-error"
    >
      {{ errorMessage }}
    </p>

    <!-- Factor Weighting subsection (read-only, inherited from EIGE). -->
    <div class="job-category-scoring__row">
      <div class="job-category-scoring__label-block">
        <span class="job-category-scoring__subsection-name">Factor Weighting</span>
        <span class="job-category-scoring__subsection-description">
          Inherited from the EIGE factor and subfactor plan and not configurable — the same for
          every account. Total Score is the straight sum of the four factor scores, out of 73
          points.
        </span>
      </div>
      <div class="job-category-scoring__weighting">
        <table class="job-category-scoring__weighting-table" data-testid="weighting-table">
          <thead>
            <tr>
              <th scope="col">Factor</th>
              <th scope="col">Maximum</th>
              <th scope="col">Share of Total</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in factorWeightingRows"
              :key="row.factor"
              :data-testid="`weighting-row-${row.factor}`"
            >
              <th scope="row">{{ row.label }}</th>
              <td :data-testid="`weighting-max-${row.factor}`">{{ row.max_points }} pts</td>
              <td :data-testid="`weighting-share-${row.factor}`">{{ row.share_percent }}%</td>
            </tr>
            <tr class="job-category-scoring__weighting-total" data-testid="weighting-total">
              <th scope="row">Total</th>
              <td data-testid="weighting-total-max">{{ totalMaxPointsDisplay }} pts</td>
              <td data-testid="weighting-total-share">{{ totalSharePercentDisplay }}%</td>
            </tr>
          </tbody>
        </table>
        <p class="job-category-scoring__hint" data-testid="weighting-note">
          The weighting follows the EIGE plan and is not configurable. Total Score is the sum of the
          four factor scores — 73 points maximum.
        </p>
      </div>
    </div>

    <!-- Job Value Bands subsection: one band-count control, derived preview. -->
    <div class="job-category-scoring__row">
      <div class="job-category-scoring__label-block">
        <span class="job-category-scoring__subsection-name">Job Value Bands</span>
        <span class="job-category-scoring__subsection-description">
          How many bands the 0–73 point Total Score is divided into. Job Categories falling in the
          same band are treated as work of equal value.
        </span>
      </div>
      <div class="job-category-scoring__bands">
        <div class="job-category-scoring__band-count">
          <label
            class="job-category-scoring__band-count-label"
            for="job-scoring-band-count"
          >
            Number of bands
          </label>
          <div
            class="job-category-scoring__input-wrap"
            :class="{ 'job-category-scoring__input-wrap--error': Boolean(bandsError) }"
          >
            <input
              id="job-scoring-band-count"
              type="number"
              min="2"
              max="10"
              step="1"
              class="job-category-scoring__input"
              v-model.number="bandCount"
              :disabled="loading || saving"
              :aria-invalid="Boolean(bandsError)"
              aria-describedby="job-scoring-band-count-hint"
              data-testid="band-count-input"
            />
          </div>
          <span
            id="job-scoring-band-count-hint"
            class="job-category-scoring__hint"
            data-testid="band-count-hint"
          >
            Between 2 and 10
          </span>
        </div>

        <p
          v-if="bandsError"
          class="job-category-scoring__bands-error"
          data-testid="bands-error"
          role="alert"
        >
          {{ bandsError }}
        </p>

        <div class="job-category-scoring__band-preview" data-testid="band-preview">
          <span class="job-category-scoring__band-preview-title">Resulting bands (points)</span>
          <div class="job-category-scoring__band-chips">
            <span
              v-for="(band, index) in previewBands"
              :key="index"
              class="job-category-scoring__band-chip"
              :data-testid="`band-preview-${index}`"
            >
              {{ formatBandRange(band) }}
            </span>
          </div>
        </div>

        <p class="job-category-scoring__hint" data-testid="bands-note">
          Bands are generated from this count, not stored. Each band is identified by its point
          range only — no name and no number. Both ends are inclusive.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { getJobScoringConfig, updateJobScoringConfig } from "@/api/jobScoringAPI";
import {
  BAND_COUNT_DEFAULT,
  deriveJobValueBands,
  formatBandRange,
  isValidBandCount,
} from "@components/constants/jobValueBands";

/**
 * Display labels for the factor keys the config API returns. The read-only
 * weighting rows themselves come from `factor_weighting` on the config payload,
 * so the maxima and shares cannot drift from the backend constants.
 */
const FACTOR_LABELS = {
  skills: "Skills",
  responsibility: "Responsibility",
  effort: "Effort",
  workingConditions: "Working Conditions",
};

const props = defineProps({
  /**
   * Account id to load job scoring configuration for. Falls back to the
   * `currentAccountID` localStorage value used by the rest of the Settings flow.
   */
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const config = ref(null);
const bandCount = ref(BAND_COUNT_DEFAULT);
const savedBandCount = ref(BAND_COUNT_DEFAULT);

const loading = ref(false);
const saving = ref(false);
const errorMessage = ref("");

function resolveAccountId() {
  if (props.accountId !== null && props.accountId !== undefined && props.accountId !== "") {
    return props.accountId;
  }
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

function extractSingleData(response) {
  const data = response?.data;
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }
  if (data && Array.isArray(data.results)) {
    return data.results[0] ?? null;
  }
  return data ?? null;
}

function applyConfig(source) {
  config.value = source && typeof source === "object" ? source : null;
  const stored = config.value ? config.value.band_count : undefined;
  const next = isValidBandCount(stored) ? Number(stored) : BAND_COUNT_DEFAULT;
  bandCount.value = next;
  savedBandCount.value = next;
}

const factorWeightingRows = computed(() => {
  const rows = Array.isArray(config.value?.factor_weighting) ? config.value.factor_weighting : [];
  return rows.map((row) => ({
    factor: row.factor,
    label: FACTOR_LABELS[row.factor] ?? row.factor,
    max_points: row.max_points,
    share_percent: row.share_percent,
  }));
});

const totalMaxPointsDisplay = computed(() =>
  config.value && config.value.total_max_points != null ? config.value.total_max_points : "–"
);

const totalSharePercentDisplay = computed(() =>
  config.value && config.value.total_share_percent != null ? config.value.total_share_percent : "–"
);

/** The 0–73 scale the preview tiles; no local 73 literal — use the API maximum. */
const totalMaxPoints = computed(() => Number(config.value?.total_max_points));

const previewBands = computed(() => deriveJobValueBands(bandCount.value, totalMaxPoints.value));

const bandsError = computed(() => {
  if (!isValidBandCount(bandCount.value)) {
    return "Number of bands must be a whole number between 2 and 10.";
  }
  return "";
});

const canSave = computed(() => {
  return config.value !== null && !loading.value && !saving.value && !bandsError.value;
});

async function fetchScoring() {
  const accountId = resolveAccountId();
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to load job scoring for.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const configResponse = await getJobScoringConfig(accountId);
    applyConfig(extractSingleData(configResponse));
  } catch {
    applyConfig(null);
    errorMessage.value = "Failed to load job scoring configuration. Please try again.";
  } finally {
    loading.value = false;
  }
}

async function handleSave() {
  if (!canSave.value || saving.value) {
    return;
  }
  const accountId = resolveAccountId();
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to update.";
    return;
  }

  saving.value = true;
  errorMessage.value = "";
  try {
    await updateJobScoringConfig(accountId, { band_count: Number(bandCount.value) });
    await fetchScoring();
  } catch {
    errorMessage.value = "Failed to save job scoring configuration. Please try again.";
  } finally {
    saving.value = false;
  }
}

function handleCancel() {
  if (saving.value || loading.value) {
    return;
  }
  bandCount.value = savedBandCount.value;
  errorMessage.value = "";
}

onMounted(fetchScoring);

defineExpose({
  config,
  factorWeightingRows,
  bandCount,
  previewBands,
  bandsError,
  canSave,
  errorMessage,
  handleSave,
  handleCancel,
});
</script>

<style scoped>
.job-category-scoring {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.job-category-scoring__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.job-category-scoring__header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.job-category-scoring__title {
  margin: 0;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.job-category-scoring__subtitle {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.job-category-scoring__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.job-category-scoring__button {
  padding: 10px 14px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.job-category-scoring__button:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.job-category-scoring__button--cancel {
  background: #ffffff;
  color: #344054;
  border: 1px solid #d0d5dd;
}

.job-category-scoring__button--cancel:hover:not(:disabled) {
  background: #f9fafb;
}

.job-category-scoring__button--save {
  background: #7f56d9;
  color: #ffffff;
  border: 1px solid #7f56d9;
}

.job-category-scoring__button--save:hover:not(:disabled) {
  background: #6941c6;
  border-color: #6941c6;
}

.job-category-scoring__button:disabled {
  background: #f2f4f7;
  color: #98a2b3;
  border-color: #eaecf0;
  cursor: not-allowed;
}

.job-category-scoring__error {
  margin: 0;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.job-category-scoring__row {
  display: flex;
  align-items: flex-start;
  gap: 32px;
}

.job-category-scoring__label-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 280px;
  max-width: 280px;
}

.job-category-scoring__subsection-name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #334055;
}

.job-category-scoring__subsection-description {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.job-category-scoring__weighting {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
}

.job-category-scoring__weighting-table {
  width: 100%;
  max-width: 520px;
  border-collapse: collapse;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
}

.job-category-scoring__weighting-table th,
.job-category-scoring__weighting-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eaecf0;
  color: #334055;
}

.job-category-scoring__weighting-table thead th {
  font-weight: 500;
  color: #475467;
}

.job-category-scoring__weighting-table tbody th {
  font-weight: 500;
  color: #101828;
}

.job-category-scoring__weighting-total th,
.job-category-scoring__weighting-total td {
  border-bottom: none;
  font-weight: 600;
  color: #101828;
}

.job-category-scoring__input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 88px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
}

.job-category-scoring__input-wrap:focus-within {
  border-color: #d6bbfb;
  box-shadow: 0px 0px 0px 4px rgba(158, 119, 237, 0.25);
}

.job-category-scoring__input-wrap--error {
  border-color: #d92d20;
}

.job-category-scoring__input {
  border: none;
  outline: none;
  width: 100%;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.job-category-scoring__input:disabled {
  color: #98a2b3;
}

.job-category-scoring__hint {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475767;
}

.job-category-scoring__bands {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
}

.job-category-scoring__band-count {
  display: flex;
  align-items: center;
  gap: 12px;
}

.job-category-scoring__band-count-label {
  flex: 0 0 140px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #334055;
}

.job-category-scoring__band-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.job-category-scoring__band-preview-title {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #334055;
}

.job-category-scoring__band-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.job-category-scoring__band-chip {
  padding: 4px 10px;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  background: #f9fafb;
  font-family: Inter;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  white-space: nowrap;
}

.job-category-scoring__bands-error {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #d92d20;
}
</style>
